import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * ValuesCarousel — editorial "manifesto" interaction for the Philosophy values.
 *
 * Design intent: one value at a time, centered and full-weight, with the
 * neighboring values softly peeking at the edges (low opacity, narrower).
 * Movement is deliberate — long ease-out transitions, no autoplay, no
 * carousel-app energy. Reads like turning a page in a luxury manifesto.
 *
 * Interaction:
 *   - Chevron arrows on desktop (subtle hover state).
 *   - Tactile "01 / 08" page indicator below.
 *   - Keyboard arrow keys when the section is in viewport (IntersectionObserver gated).
 *   - Swipe gestures on touch devices (≥40px threshold).
 *   - Click on a peek card to bring it to center.
 *
 * Layout: a horizontally-scrolling flex row inside `overflow-hidden`. The row
 * translates so that the active card sits dead-center. Card width is sized via
 * a CSS variable (`--card-width: min(560px, 82vw)`) so the peek behavior works
 * naturally across breakpoints.
 */
export function ValuesCarousel({ values }) {
  const [active, setActive] = useState(0);
  const sectionRef = useRef(null);
  const touchStartX = useRef(null);
  const max = values.length;

  const goPrev = useCallback(() => setActive(a => (a - 1 + max) % max), [max]);
  const goNext = useCallback(() => setActive(a => (a + 1) % max), [max]);

  // Keyboard nav — only active while the carousel is in viewport so arrow keys
  // don't hijack scrolling elsewhere on the page.
  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;
    let inView = false;
    const onKey = (e) => {
      if (!inView) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); goPrev(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); goNext(); }
    };
    const io = new IntersectionObserver(
      ([entry]) => { inView = entry.isIntersecting && entry.intersectionRatio > 0.4; },
      { threshold: [0, 0.4, 1] }
    );
    io.observe(node);
    window.addEventListener('keydown', onKey);
    return () => {
      io.disconnect();
      window.removeEventListener('keydown', onKey);
    };
  }, [goPrev, goNext]);

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) (dx < 0 ? goNext : goPrev)();
    touchStartX.current = null;
  };

  return (
    <div
      ref={sectionRef}
      className="relative"
      style={{ '--card-width': 'min(560px, 82vw)' }}
      data-testid="values-carousel"
    >
      {/* Track */}
      <div
        className="overflow-hidden"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div
          className="flex items-stretch"
          style={{
            transform: `translateX(calc(50% - var(--card-width) / 2 - ${active} * var(--card-width)))`,
            transition: 'transform 900ms cubic-bezier(0.22, 0.61, 0.36, 1)',
          }}
        >
          {values.map((v, i) => {
            const isActive = i === active;
            const distance = Math.abs(i - active);
            const isAdjacent = distance === 1;
            return (
              <button
                key={v.title}
                type="button"
                onClick={() => setActive(i)}
                aria-label={`${v.title} — value ${i + 1} of ${max}`}
                aria-current={isActive ? 'true' : 'false'}
                tabIndex={isActive ? 0 : -1}
                className="shrink-0 text-left px-8 sm:px-12 py-6 focus:outline-none cursor-pointer"
                style={{
                  width: 'var(--card-width)',
                  opacity: isActive ? 1 : (isAdjacent ? 0.22 : 0.06),
                  transform: isActive ? 'scale(1)' : 'scale(0.96)',
                  transformOrigin: 'center center',
                  transition:
                    'opacity 700ms cubic-bezier(0.22, 0.61, 0.36, 1), ' +
                    'transform 900ms cubic-bezier(0.22, 0.61, 0.36, 1)',
                  pointerEvents: isActive ? 'auto' : (isAdjacent ? 'auto' : 'none'),
                }}
                data-testid={`value-card-${v.title.toLowerCase()}`}
                data-active={isActive ? 'true' : 'false'}
              >
                <p className="font-serif text-[11px] text-[#C9A961] tracking-[0.32em] mb-8">
                  {String(i + 1).padStart(2, '0')}
                </p>
                <h3 className="font-serif text-3xl sm:text-4xl text-[#1A1A1A] mb-6 leading-[1.15]">
                  {v.title}
                </h3>
                <p className="text-base sm:text-[17px] text-[#6B6B6B] leading-[1.75]">
                  {v.desc}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Navigation — chevrons + tactile page indicator */}
      <div className="mt-14 flex items-center justify-center gap-8 sm:gap-10">
        <button
          type="button"
          onClick={goPrev}
          aria-label="Previous value"
          data-testid="values-carousel-prev"
          className="group p-2 text-[#6B6B6B] hover:text-[#1A1A1A] transition-colors"
        >
          <ChevronLeft className="w-5 h-5 transition-transform duration-500 group-hover:-translate-x-0.5" strokeWidth={1.25} />
        </button>

        <div className="flex items-baseline gap-3 font-serif text-[12px] text-[#1A1A1A] tracking-[0.28em] tabular-nums">
          <span aria-live="polite" data-testid="values-carousel-current">
            {String(active + 1).padStart(2, '0')}
          </span>
          <span className="w-8 h-px bg-[#E8E4DC]" aria-hidden="true" />
          <span className="text-[#999]">{String(max).padStart(2, '0')}</span>
        </div>

        <button
          type="button"
          onClick={goNext}
          aria-label="Next value"
          data-testid="values-carousel-next"
          className="group p-2 text-[#6B6B6B] hover:text-[#1A1A1A] transition-colors"
        >
          <ChevronRight className="w-5 h-5 transition-transform duration-500 group-hover:translate-x-0.5" strokeWidth={1.25} />
        </button>
      </div>
    </div>
  );
}
